import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Clock, Home, IndianRupee, KeyRound, Plus, ShieldCheck, Trash2, Users, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FIELDS = [
  ["name", "Unit name (e.g. Flat 302, Sunview)"],
  ["city", "City"],
  ["locality", "Locality"],
  ["address", "Address"],
  ["contact_phone", "Your contact phone"],
  ["default_rent", "Monthly rent ₹"],
  ["rent_due_day", "Rent due day of month (1-28)"],
];

const EMPTY = { name: "", city: "", locality: "", address: "", contact_phone: "", default_rent: "", rent_due_day: "5", property_id: "" };

export default function Rentals() {
  const { user } = useAuth();
  const [access, setAccess] = useState(null);
  const [units, setUnits] = useState([]);
  const [properties, setProperties] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/stay/access");
      setAccess(data);
      if (data.rental_granted) {
        const res = await api.get("/stays", { params: { kind: "rental" } });
        setUnits(res.data);
        api.get("/properties/mine").then((r) => setProperties(r.data || [])).catch(() => setProperties([]));
      }
    } catch (err) {
      toast.error(errText(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const requestAccess = async (scope) => {
    setBusy(true);
    try {
      const { data } = await api.post("/stay/access-request", null, { params: { scope } });
      toast.success(data.status === "granted" ? "Access already granted" : "Request sent to the admin");
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const createUnit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.city.trim()) return toast.error("Unit name and city are required");
    setBusy(true);
    try {
      const payload = {
        ...form,
        kind: "rental",
        default_rent: Number(form.default_rent || 0),
        rent_due_day: Number(form.rent_due_day || 5),
      };
      if (editing) await api.put(`/stays/${editing}`, payload);
      else await api.post("/stays", payload);
      toast.success(editing ? "Rental details updated" : "Rental unit added");
      resetForm();
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const resetForm = () => {
    setForm(EMPTY);
    setEditing(null);
    setCreating(false);
  };

  const startEdit = (s) => {
    setForm({
      name: s.name || "", city: s.city || "", locality: s.locality || "", address: s.address || "",
      contact_phone: s.contact_phone || "", default_rent: String(s.default_rent ?? ""),
      rent_due_day: String(s.rent_due_day ?? 5), property_id: s.property_id || "",
    });
    setEditing(s.stay_id);
    setCreating(true);
    setTimeout(() => document.getElementById("rental-form")?.scrollIntoView({ behavior: "smooth", block: "center" }), 60);
  };

  const removeUnit = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? Its tenant and rent records will be removed.`)) return;
    try {
      await api.delete(`/stays/${id}`);
      toast.success("Rental unit removed");
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  if (!access)
    return (
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-16">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );

  if (!access.rental_granted)
    return (
      <div className="mx-auto max-w-[720px] px-5 md:px-10 py-16" data-testid="rentals-access-gate">
        <div className="rounded-2xl border border-border p-8">
          <ShieldCheck className="h-7 w-7 text-primary" strokeWidth={1.8} />
          <h1 className="mt-5 font-display text-2xl">Rentals</h1>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
            Track tenants and monthly rent for the flats, houses and shops you've let out. Rentals access is
            granted separately by the Houses7.com admin — ask for rentals only, or for both modules at once.
          </p>
          {access.status === "pending" && (access.requested_scopes || []).includes("rental") ? (
            <p className="mt-6 flex items-center gap-2 rounded-xl bg-muted px-4 py-3 text-sm" data-testid="rentals-request-pending">
              <Clock className="h-4 w-4 text-primary" /> Request pending — the admin will approve it shortly.
            </p>
          ) : (
            <div className="mt-6 flex flex-wrap gap-2">
              <Button onClick={() => requestAccess("rental")} disabled={busy} className="h-12 rounded-lg font-bold hover:brightness-95" data-testid="rentals-request-access-btn">
                {busy ? "Sending…" : "Request rentals access"}
              </Button>
              {!access.granted && (
                <Button onClick={() => requestAccess("both")} disabled={busy} variant="outline" className="h-12 rounded-lg font-bold" data-testid="rentals-request-both-btn">
                  Request both (PG + rentals)
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    );

  return (
    <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-10" data-testid="rentals-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl">Rentals</h1>
          <p className="mt-1 text-[13px] text-muted-foreground">
            {user?.role === "admin" ? "All rental units on Houses7.com" : "Flats, houses and shops you've let out on rent"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/stays/collections">
            <Button variant="outline" className="h-12 rounded-lg font-bold" data-testid="rentals-collections-link">
              <IndianRupee className="h-4 w-4 mr-2" /> Collections board
            </Button>
          </Link>
          <Button className="h-12 rounded-lg font-bold hover:brightness-95" onClick={() => (creating ? resetForm() : setCreating(true))} data-testid="rental-add-toggle-btn">
            <Plus className="h-4 w-4 mr-2" /> Add a rental unit
          </Button>
        </div>
      </div>

      {creating && (
        <form onSubmit={createUnit} id="rental-form" className="mt-7 rounded-2xl border border-border p-6 md:p-8" data-testid="rental-create-form">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-display text-lg">{editing ? "Edit rental details" : "New rental unit"}</h2>
            {editing && (
              <button type="button" onClick={resetForm} className="text-[12px] font-semibold underline" data-testid="rental-cancel-edit">
                Cancel
              </button>
            )}
          </div>
          <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FIELDS.map(([k, label]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-xs font-semibold">{label}</Label>
                <Input
                  value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className="h-12 rounded-lg"
                  data-testid={`rental-${k.replace(/_/g, "-")}-input`}
                />
              </div>
            ))}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Link a listing (optional)</Label>
              <select
                value={form.property_id}
                onChange={(e) => setForm({ ...form, property_id: e.target.value })}
                className="h-12 w-full rounded-lg border border-input bg-background px-3 text-sm"
                data-testid="rental-property-select"
              >
                <option value="">Not linked</option>
                {properties.map((p) => (
                  <option key={p.property_id} value={p.property_id}>
                    {p.title} · {p.city}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-muted-foreground">The listing shows as occupied once you add a tenant.</p>
            </div>
          </div>
          <Button type="submit" disabled={busy} className="mt-6 h-12 rounded-lg font-bold hover:brightness-95" data-testid="rental-create-btn">
            {busy ? "Saving…" : editing ? "Save changes" : "Create rental unit"}
          </Button>
        </form>
      )}

      {units.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-border p-10" data-testid="rentals-empty">
          <KeyRound className="h-7 w-7 text-primary" strokeWidth={1.8} />
          <p className="mt-4 font-display text-xl">No rental unit yet</p>
          <p className="mt-2 text-sm text-muted-foreground">
            Add a flat, house or shop you've let out — we'll track the tenant, monthly rent and overdue months for you.
          </p>
        </div>
      ) : (
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="rentals-grid">
          {units.map((s) => (
            <div key={s.stay_id} className="rounded-2xl border border-border p-6" data-testid={`rental-card-${s.stay_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <Link to={`/rentals/${s.stay_id}`} className="font-display text-lg hover:underline">
                    {s.name}
                  </Link>
                  <p className="mt-1 text-[13px] text-muted-foreground">{[s.locality, s.city].filter(Boolean).join(", ")}</p>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-lg"
                    onClick={() => startEdit(s)}
                    data-testid={`rental-edit-${s.stay_id}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-lg text-destructive"
                    onClick={() => removeUnit(s.stay_id, s.name)}
                    data-testid={`rental-delete-${s.stay_id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-2 text-[12px] font-semibold">
                <span className="flex items-center gap-1.5 rounded-full bg-muted px-3 py-1.5">
                  <Users className="h-3.5 w-3.5" /> {s.occupied_beds} of {s.total_beds} occupied
                </span>
                <span className="flex items-center gap-1.5 rounded-full bg-muted px-3 py-1.5">
                  <Home className="h-3.5 w-3.5" /> ₹ {Number(s.default_rent || 0).toLocaleString("en-IN")} / month
                </span>
                <span
                  className={`rounded-full px-3 py-1.5 ${s.unpaid_beds ? "bg-destructive/10 text-destructive" : "bg-emerald-500/10 text-emerald-700"}`}
                  data-testid={`rental-unpaid-${s.stay_id}`}
                >
                  {s.unpaid_beds ? `${s.unpaid_beds} rent due` : "All rent paid"}
                </span>
              </div>

              <Link to={`/rentals/${s.stay_id}`}>
                <Button variant="outline" className="mt-5 h-11 w-full rounded-lg" data-testid={`rental-manage-${s.stay_id}`}>
                  Manage tenant & rent
                </Button>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
